import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getTextReplacements,
  createTextReplacement,
  patchTextReplacement,
  deleteTextReplacement,
  TextReplacementRule,
  CreateTextReplacementRequest,
  UpdateTextReplacementRequest,
} from '@/features/internal/text-replacements/apis/textReplacements'
import { useToast } from '@/shared/contexts/ToastContext'

interface UseTextReplacementsQueryReturn {
  rules: TextReplacementRule[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * テキスト置換ルール一覧取得クエリ（TanStack Query）
 *
 * 責務:
 * - テキスト置換ルール一覧の取得
 * - キャッシュ管理
 *
 * 使用例:
 * ```tsx
 * const { rules, isLoading, error } = useTextReplacementsQuery()
 * ```
 */
export const useTextReplacementsQuery = (): UseTextReplacementsQueryReturn => {
  const query = useQuery({
    queryKey: queryKeys.textReplacements.all,
    queryFn: async (): Promise<TextReplacementRule[]> => {
      const data = await getTextReplacements()
      return data
    },
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  })

  return {
    rules: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: () => query.refetch(),
  }
}

/**
 * テキスト置換ルール作成ミューテーション
 */
export const useCreateTextReplacementMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (
      data: CreateTextReplacementRequest
    ): Promise<TextReplacementRule> => {
      return await createTextReplacement(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.textReplacements.all,
      })
      showToast('ルールを作成しました', 'success')
    },
    onError: () => {
      showToast('ルールの作成に失敗しました', 'error')
    },
  })
}

/**
 * テキスト置換ルール更新ミューテーション
 */
export const useUpdateTextReplacementMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: UpdateTextReplacementRequest
    }): Promise<TextReplacementRule> => {
      return await patchTextReplacement(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.textReplacements.all,
      })
      showToast('ルールを更新しました', 'success')
    },
    onError: () => {
      showToast('ルールの更新に失敗しました', 'error')
    },
  })
}

/**
 * テキスト置換ルール削除ミューテーション
 */
export const useDeleteTextReplacementMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await deleteTextReplacement(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.textReplacements.all,
      })
      showToast('ルールを削除しました', 'success')
    },
    onError: () => {
      showToast('ルールの削除に失敗しました', 'error')
    },
  })
}
